import Vue from "vue";
import VueRouter, { RawLocation, Route, RouteConfig } from "vue-router";
import { AuthService } from "@/services/auth.service";
import { TestModule } from "@/store/test.module";
import { AuthModule } from "@/store/auth.module";
import { UserPermissions } from "@/config/consts";

// catching errors
const originalPush = VueRouter.prototype.push as (
  location: RawLocation,
) => Promise<Route>;
VueRouter.prototype.push = async function(location: RawLocation) {
  return originalPush.call(this, location).catch(err => {
    if (err.name == "NavigationDuplicated") return err;
    throw err;
  });
};

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    meta: {
      isPublic: false,
    },
    redirect: { name: "f-redirect" },
    component: () => import("@/views/App/AppMain.vue"),
    children: [
      {
        path: "profile",
        redirect: "profile/organization",
        component: () => import("@/views/Profile/ProfileView.vue"),
        meta: {
          isPublic: false,
        },
        children: [
          {
            path: "organization",
            name: "profile-organization",
            component: () => import("@/views/Profile/OrganizationPage.vue"),
            meta: {
              isPublic: false,
            },
          },
          {
            path: "users",
            name: "users-management",
            component: () => import("@/views/Profile/UsersManagementPage.vue"),
            meta: {
              isPublic: false,
              action: UserPermissions.USERS_MANAGEMENT,
            },
          },
          {
            path: "users/:user_rid",
            name: "user-page",
            component: () => import("@/views/Profile/UserPage.vue"),
            meta: {
              isPublic: false,
              action: UserPermissions.USERS_MANAGEMENT,
            },
          },
        ],
      },
      {
        path: "contracts",
        component: () => import("@/views/Contract/ContractsView.vue"),
        name: "contracts",
        meta: {
          isPublic: false,
        },
      },
      {
        path: "contracts/:id",
        component: () => import("@/views/Contract/Menu/MenuView.vue"),
        meta: {
          isPublic: false,
        },
        children: [
          // menu items
          {
            path: "appeal",
            component: () => import("@/views/Contract/Menu/Appeals.vue"),
            name: "appeal",
          },
          {
            path: "new-appeal",
            component: () => import("@/views/Contract/Menu/AppealNew.vue"),
            name: "new-appeal",
          },
          {
            path: "edit-appeal/:RequestId",
            component: () => import("@/views/Contract/Menu/AppealNew.vue"),
            name: "edit-appeal",
          },
          {
            path: "appeal-in/:RequestId",
            component: () => import("@/views/Contract/Menu/AppealIn.vue"),
            name: "appeal-in",
          },
          {
            path: "/",
            component: () => import("@/views/Contract/Menu/ContractView.vue"),
            name: "contract",
          },
          {
            path: "abonent",
            component: () => import("@/views/Contract/Menu/AbonentView.vue"),
            name: "abonent",
          },
          {
            path: "accounting-points",
            component: () =>
              import("@/views/Contract/Menu/AccountingPointsView.vue"),
            name: "accounting-points",
          },
          {
            path: "point/:pointId",
            component: () => import("@/views/Contract/Menu/PointView.vue"),
            name: "point",
          },
          {
            path: "payments",
            component: () => import("@/views/Contract/Menu/PaymentsView.vue"),
            name: "payments",
          },
          {
            path: "report-forms",
            component: () =>
              import("@/views/Contract/Menu/ReportFormsView.vue"),
            name: "report-forms",
          },
          {
            path: "actions",
            component: () => import("@/views/Contract/Menu/Actions.vue"),
            name: "actions",
          },
          {
            path: "debt-notifications",
            component: () =>
              import("@/views/Contract/Menu/DebtNotificationsView.vue"),
            name: "debt-notifications",
          },
          {
            path: "invoices",
            component: () => import("@/views/Contract/Menu/InvoicesView.vue"),
            name: "invoices",
            meta: {
              action: UserPermissions.SHOW_INVOICES,
            },
          },
          {
            path: "sendcounts",
            component: () =>
              import("@/views/Contract/Menu/ActionSendCount.vue"),
            name: "sendcounts",
          },
          {
            path: "reconciliation-act",
            component: () =>
              import("@/views/Contract/Menu/actions/ReconciliationActView.vue"),
            name: "reconciliation-act",
          },
          {
            path: "invoice-act",
            component: () =>
              import("@/views/Contract/Menu/actions/InvoiceActView.vue"),
            name: "invoice-act",
          },
          {
            path: "reconciliation-act-netto",
            component: () =>
              import(
                "@/views/Contract/Menu/actions/ReconciliationActNettoView.vue"
              ),
            name: "reconciliation-act-netto",
          },
          {
            path: "exp-invoice",
            component: () =>
              import("@/views/Contract/Menu/actions/ExpInvoiceView.vue"),
            name: "exp-invoice",
          },
          {
            path: "contract-vie",
            component: () =>
              import("@/views/Contract/Menu/actions/ContractVieView.vue"),
            name: "contract-vie",
          },
          {
            path: "change-plan",
            component: () =>
              import("@/views/Contract/Menu/actions/ChangePlanView.vue"),
            name: "change-plan",
          },
          {
            path: "change-plan-new",
            component: () =>
              import("@/views/Contract/Menu/actions/ChangePlanAddEditView.vue"),
            name: "change-plan-new",
          },
          {
            path: "change-plan-edit/:idplan",
            component: () =>
              import("@/views/Contract/Menu/actions/ChangePlanAddEditView.vue"),
            name: "change-plan-edit",
          },
        ],
      },
      {
        name: "f-redirect",
        path: "redirect",
        component: () => import("@/views/Functional/FRedirect.vue"),
      },
    ],
  },
  {
    path: "/auth",
    component: () => import("@/views/Auth/AuthView.vue"),
    meta: {
      isPublic: true,
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/verify",
    component: () => import("@/views/Functional/FVerify.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/updatePassword",
    component: () => import("@/views/UpdatePassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/restorePassword",
    component: () => import("@/views/UpdatePassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/ForgotPassword",
    component: () => import("@/views/Auth/ForgotPassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/warningRestore",
    component: () => import("@/views/Auth/WarningRestore.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/passChanged",
    component: () => import("@/views/Auth/PassChanged.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/getDoc/:guid",
    component: () => import("@/views/GetDoc.vue"),
    meta: {
      isPublic: true,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  TestModule.setEdsCheckDisabled(to.query.debug !== undefined);

  const hasToken = AuthService.hasToken();
  const isPublic = to.matched.some(record => record.meta.isPublic);
  const onlyWhenLoggedOut = to.matched.some(
    record => record.meta.onlyWhenLoggedOut,
  );

  if (hasToken && !to.name && to.path == "/verify") {
    AuthService.logout(false);
    return next({
      path: to.fullPath,
    });
  }

  if (
    hasToken &&
    !to.name &&
    to.path &&
    (to.path.toLowerCase().indexOf("getdoc") > 0 ||
      to.path.indexOf("updatePassword") > 0)
  ) {
    AuthService.logout(false);
    return next({
      path: to.fullPath,
    });
  }

  // has token and route isn't defined (issue when redirecting from esalmaty)
  if (hasToken && !to.name) {
    await AuthModule.getUser();
    if (AuthModule.user) {
      return next({ path: "/" });
    }

    return next({ path: "/auth" });
  }

  // has no local token & is on a public path
  if (!hasToken && !isPublic) {
    return next({
      path: "/auth",
    });
  }

  // has token but is on onlyWhenLoggedOut page
  if (hasToken && onlyWhenLoggedOut) {
    // go to the profile
    return next({
      path: "/profile",
    });
  }

  if (hasToken && to.meta?.action) {
    if (!AuthModule.hasPermissionTo(to.meta.action)) {
      return next({
        path: "/contracts",
      });
    }
  }

  // yandex metrika
  window.ym(70323007, "hit", to.path);

  next();
});

export default router;
